import { queryClient } from "@/client";
import { GetListsResultDto } from "@advanced-sales-technologies/backend-client/models/GetListsResultDto";
import { InfiniteData, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { HiOutlineCheckCircle } from "react-icons/hi2";

import { InfiniteListsParams } from "@/hooks/api/list/useInfiniteLists";
import useSalesyApiClient from "@/hooks/useSalesyApiClient";
import { useToast } from "@/hooks/useToast";

type DeleteListBody = {
  listIds: string[];
};

type UseDeleteListOptions = {
  listFilter: InfiniteListsParams;
  displayToast?: boolean;
};

export const useDeleteList = ({ listFilter, displayToast = true }: UseDeleteListOptions) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { apiClient } = useSalesyApiClient();

  const deleteLists = async (requestBody: DeleteListBody) => {
    return apiClient.lists.delete({
      idsArray: requestBody.listIds,
    });
  };

  const mutation = useMutation({
    mutationKey: ["delete-lists"],
    mutationFn: deleteLists,
    onSuccess: (_, variables) => {
      // Update the query data by removing the deleted items
      queryClient.setQueryData<InfiniteData<GetListsResultDto>>(["lists", listFilter], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: page.data.filter((item) => !variables.listIds.includes(item.id)),
          })),
        };
      });

      // Show a success toast notification
      if (displayToast) {
        showToast({
          featuredIcon: { icon: HiOutlineCheckCircle, color: "success" },
          heading: t("list.delete.success", { count: variables.listIds.length }),
        });
      }
    },
  });

  const { mutate, ...mutationResult } = mutation;

  return {
    ...mutationResult,
    deleteList: mutate,
  };
};
